import { Badge, Checkbox, FooterCell, Stack, Typography } from '@ui-components/neo.tax';
import { useMemo, ReactElement } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import {
  getPath,
  Path,
  HIGHLIGHT_VARIANT,
  ProjectLeftBar,
  ProjectName,
  TABLE_CELL_PLACEHOLDER,
  TableHeader,
  useCurrentCompanyId,
  useHasSkippedConnectingTicketing,
} from '@neo.tax/shared';
import { useSredContext } from '@neo.tax/sred/context';
import { SredProject } from '@neo.tax/sred/hooks/useSredProjects';
import {
  getAiGuidanceBadgeConfig,
  getSredCategoryBadgeConfig,
  getSredDocumentationStatus,
  ProjectBadgeConfig,
} from '@neo.tax/sred/utils';

import { SredDocumentationBadgeConfig } from '../sred-projects-layout.types';
import { getSredDetailsBadgeConfig } from '../sred-projects-layout.utils';

interface UseSredProjectsTableColumnsProps<T extends Record<string, any>> {
  avgTicketCount: number;
  clickedRowId?: string;
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: T) => void;
  rowCount: number;
  selectedRowIds: string[];
}

export const useSredProjectsTableColumns = ({
  avgTicketCount,
  clickedRowId,
  onSelectAllRows,
  onSelectRow,
  rowCount,
  selectedRowIds,
}: UseSredProjectsTableColumnsProps<SredProject>): Column<SredProject>[] => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId } = useSredContext();
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();

  const columns = useMemo<Column<SredProject>[]>(() => {
    const handleSelectRow =
      (cell: CellProps<SredProject, SredProject> & UseRowSelectInstanceProps<SredProject>) =>
      (checked: boolean): void => {
        cell.toggleRowSelected(cell.value.id, checked);
        onSelectRow(checked, cell.value);
      };

    const baseColumns: Column<SredProject>[] = [
      {
        id: 'name',
        accessor: (datum: SredProject): SredProject => datum,
        width: 'minmax(200px, 360px)',
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="projects"
              name="projects"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={onSelectAllRows}
            />
            <TableHeader>Projects</TableHeader>
          </Stack>
        ),
        Cell: (cell: CellProps<SredProject, SredProject> & UseRowSelectInstanceProps<SredProject>): ReactElement => (
          <>
            <ProjectLeftBar $variant={HIGHLIGHT_VARIANT} $isClicked={clickedRowId === cell.value.id} />
            <Checkbox
              id={cell.value.id}
              name={cell.value.name}
              checked={selectedRowIds?.includes(cell.value.id)}
              onChange={handleSelectRow(cell)}
            />
            <ProjectName
              $variant={HIGHLIGHT_VARIANT}
              title={cell.value.name}
              to={getPath({
                path: Path.SRED_PROJECTS,
                companyId,
                fiscalYearId,
                sredProjectId: cell.value.id,
              })}
            >
              {cell.value.name}
            </ProjectName>
          </>
        ),
        Footer: (): ReactElement => <FooterCell preText="COUNT" text={rowCount} />,
      },
      {
        id: 'productName',
        width: 180,
        Header: () => <TableHeader>Product</TableHeader>,
        accessor: (datum: SredProject): SredProject['product'] => datum.product,
        Cell: ({ value }: CellProps<SredProject, SredProject['product']>): ReactElement =>
          value ? (
            <Badge colorVariant="yellow" dot={false} maxWidth={150}>
              {value.name}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'selectedCategory',
        width: 180,
        accessor: (datum: SredProject): ProjectBadgeConfig | undefined => getSredCategoryBadgeConfig(datum),
        Header: () => (
          <TableHeader title="The project category you selected in the Project drawer.">Selected Category</TableHeader>
        ),
        Cell: ({ value }: CellProps<SredProject, ProjectBadgeConfig | undefined>): ReactElement =>
          value ? (
            <Badge colorVariant={value.colorVariant} dot={false} title={value.description}>
              {value.label}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'aiGuidance',
        width: 180,
        accessor: (datum: SredProject): ProjectBadgeConfig | undefined => getAiGuidanceBadgeConfig(datum.aiGuidance),
        Header: () => (
          <TableHeader title="Automated guidance on project eligibility for the SR&ED credit. Only available when you connect ticketing.">
            AI Guidance
          </TableHeader>
        ),
        Cell: ({ value }: CellProps<SredProject, ProjectBadgeConfig | undefined>): ReactElement =>
          value ? (
            <Badge colorVariant={value.colorVariant} dot={false} title={value.description}>
              {value.label}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'sredDetails',
        width: 130,
        Header: () => <TableHeader>SR&ED Details</TableHeader>,
        accessor: (datum: SredProject): SredDocumentationBadgeConfig | undefined =>
          getSredDetailsBadgeConfig(datum.consideredForSred, getSredDocumentationStatus(datum)),
        Cell: ({ value }: CellProps<SredProject, SredDocumentationBadgeConfig | undefined>): ReactElement =>
          value ? (
            <Badge colorVariant={value.colorVariant} dot={false} title={value.description}>
              {value.label}
            </Badge>
          ) : (
            <></>
          ),
      },
    ];

    if (hasSkippedConnectingTicketing) {
      return baseColumns;
    }
    return [
      ...baseColumns,
      {
        id: 'teams',
        width: 150,
        Header: (): ReactElement => <TableHeader>Teams</TableHeader>,
        accessor: (datum: SredProject): string => datum.workstreamNamesSearch ?? '',
        Cell: ({ row }: CellProps<SredProject, string>): ReactElement => {
          const teams = row.original.workstreamNames ?? [];
          return (
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={teams.join(', ')}>
              {teams.join(', ')}
            </Typography>
          );
        },
      },
      {
        id: 'tickets',
        width: 120,
        Header: (): ReactElement => <TableHeader textAlign="right">Tickets</TableHeader>,
        accessor: (datum: SredProject): number => datum.ticketCount,
        Cell: ({ value }: CellProps<SredProject, number>): ReactElement => (
          <Stack fullWidth justifyContent="end">
            <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
              {value}
            </Typography>
          </Stack>
        ),
        Footer: (): ReactElement => <FooterCell alignText="end" preText="AVG" text={`${Math.round(avgTicketCount)}`} />,
      },
      {
        id: 'owner',
        width: 150,
        Header: (): ReactElement => <TableHeader textAlign="left">Owner</TableHeader>,
        accessor: (datum: SredProject): string => datum.projectOwner?.fullName ?? '',
        Cell: ({ value }: CellProps<SredProject, string>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
    ];
  }, [
    hasSkippedConnectingTicketing,
    onSelectRow,
    selectedRowIds,
    rowCount,
    onSelectAllRows,
    clickedRowId,
    companyId,
    fiscalYearId,
    avgTicketCount,
  ]);

  return columns;
};
